import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Payment } from "@/services/payment";
import { EPaymentStatus, EPaymentMethod } from "@/services/payment";
import { Loader2 } from "lucide-react";

export interface EditPaymentFormData {
  paymentDate: string;
  status: EPaymentStatus;
  amount: number;
  paymentMethod: EPaymentMethod;
  notes: string;
}

interface EditPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: Payment | null;
  onSave: (data: EditPaymentFormData) => void;
  isPending?: boolean;
}

export function EditPaymentModal({
  open,
  onOpenChange,
  payment,
  onSave,
  isPending = false,
}: EditPaymentModalProps) {
  const [form, setForm] = useState<EditPaymentFormData>({
    paymentDate: "",
    status: EPaymentStatus.COMPLETED,
    amount: 0,
    paymentMethod: EPaymentMethod.TRANSFER,
    notes: "",
  });

  useEffect(() => {
    if (open && payment) {
      setForm({
        paymentDate: payment.paymentDate.split("T")[0],
        status: payment.status,
        amount: payment.amount,
        paymentMethod: payment.paymentMethod,
        notes: payment.notes || "",
      });
    }
  }, [open, payment]);

  const handleSave = () => {
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Editar Pago</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="paymentDate">Fecha de pago</Label>
              <Input
                id="paymentDate"
                type="date"
                value={form.paymentDate}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    paymentDate: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Monto</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    amount: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="paymentMethod">Método de pago</Label>
              <Select
                value={form.paymentMethod}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    paymentMethod: value as EPaymentMethod,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un método" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EPaymentMethod.TRANSFER}>Transferencia</SelectItem>
                  <SelectItem value={EPaymentMethod.CASH}>Efectivo</SelectItem>
                  <SelectItem value={EPaymentMethod.NEQUI}>Nequi</SelectItem>
                  <SelectItem value={EPaymentMethod.DAVIPLATA}>Daviplata</SelectItem>
                  <SelectItem value={EPaymentMethod.BANCOLOMBIA}>Bancolombia</SelectItem>
                  <SelectItem value={EPaymentMethod.PAYPAL}>Paypal</SelectItem>
                  <SelectItem value={EPaymentMethod.BINANCE}>Binance</SelectItem>
                  <SelectItem value={EPaymentMethod.YAPE}>Yape</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paymentStatus">Estado</Label>
              <Select
                value={form.status}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    status: value as EPaymentStatus,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EPaymentStatus.COMPLETED}>Completado</SelectItem>
                  <SelectItem value={EPaymentStatus.PENDING}>Pendiente</SelectItem>
                  <SelectItem value={EPaymentStatus.CANCELLED}>Cancelado</SelectItem>
                  <SelectItem value={EPaymentStatus.REFUNDED}>Reembolsado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              placeholder="Notas adicionales sobre el pago..."
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
